import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { upload } from "@vercel/blob/client";
import { useAdminBlogPosts } from "../hooks/useAdminBlogPosts";
import { useLanguage } from "../hooks/useLanguage";
import { useT } from "../hooks/useT";
import { BlogContent } from "./BlogPostPage";

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

function extensionForMime(mime) {
  if (mime === "image/jpeg") return ".jpg";
  if (mime === "image/png") return ".png";
  if (mime === "image/webp") return ".webp";
  return "";
}

// Collision-safety itself comes from the server's addRandomSuffix (see
// api/blog-upload.js) — this only produces a readable, safe base name.
function sanitizeFilenameForPathname(name) {
  const match = /^(.*?)(\.[^./]+)?$/.exec(name || "");
  const base = match?.[1] || "";
  const ext = (match?.[2] || "").toLowerCase();
  const safeBase =
    base
      .toLowerCase()
      .replace(/[^\w-]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "") || "image";
  return `${safeBase}${ext}`;
}

function formatMoney(value) {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-IE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("is-IS");
  } catch {
    return value;
  }
}

function StatRow({ label, value, muted = false }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-slate-200/70 py-3 last:border-b-0 dark:border-slate-800/80">
      <div
        className={`text-sm ${
          muted ? "text-slate-500 dark:text-slate-400" : "text-slate-600 dark:text-slate-300"
        }`}
      >
        {label}
      </div>
      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{value}</div>
    </div>
  );
}

function Card({ title, subtitle, children }) {
  return (
    <section
      className="
        rounded-3xl border border-slate-200/70 bg-white/75 p-5 shadow-sm backdrop-blur
        dark:border-slate-800/80 dark:bg-slate-900/75
      "
    >
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
        {subtitle ? (
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
        ) : null}
      </div>
      <div>{children}</div>
    </section>
  );
}

function SummaryPill({ label, value }) {
  return (
    <div
      className="
        rounded-2xl border border-slate-200/70 bg-white/80 px-4 py-3 shadow-sm
        dark:border-slate-800/80 dark:bg-slate-900/80
      "
    >
      <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="mt-1 text-xl font-semibold text-slate-900 dark:text-slate-100">{value}</div>
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
      {children}
    </label>
  );
}

function TextInput(props) {
  return (
    <input
      {...props}
      className={`w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none ring-0 focus:border-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 ${
        props.disabled ? "cursor-not-allowed opacity-60" : ""
      }`}
    />
  );
}

function TextArea(props) {
  return (
    <textarea
      {...props}
      className={`w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none ring-0 focus:border-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 ${
        props.disabled ? "cursor-not-allowed opacity-60" : ""
      }`}
    />
  );
}

function Select(props) {
  return (
    <select
      {...props}
      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none ring-0 focus:border-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
    />
  );
}

function GenerateDraftCard({ onGenerated }) {
  const [form, setForm] = useState({
    type: "weather_comparison",
    lang: "en",
    baseCampsite: "",
    compareCampsite: "",
    region: "",
    forecastRawInput: "",
    coverImage: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const needsCompare = form.type === "weather_comparison";
  const disabled =
    loading ||
    !form.baseCampsite.trim() ||
    !form.region.trim() ||
    (needsCompare && !form.compareCampsite.trim());

  async function handleGenerate() {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const isComparison = form.type === "weather_comparison";

      const res = await fetch("/api/admin", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "generateDraft",
          type: form.type,
          lang: form.lang,
          coverImage: form.coverImage.trim(),
          context: {
            baseCampsite: form.baseCampsite.trim(),
            compareCampsite: isComparison ? form.compareCampsite.trim() : "",
            region: form.region.trim(),
            forecastRawInput: form.forecastRawInput.trim(),
          },
        }),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || "Failed to generate draft");
      }

      const isTitle = json?.draft?.title || "Untitled";
      setSuccess(json?.draftEn ? `Draft pair created: "${isTitle}" (IS + EN)` : `Draft created: "${isTitle}" (IS)`);

      setForm((prev) => ({
        ...prev,
        baseCampsite: "",
        compareCampsite: "",
        region: "",
        forecastRawInput: "",
        coverImage: "",
      }));

      if (typeof onGenerated === "function") {
        await onGenerated(json.draft);
      }
    } catch (err) {
      setError(err?.message || "Failed to generate draft");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="rounded-3xl border border-slate-200/70 bg-white/80 p-5 shadow-sm dark:border-slate-800/80 dark:bg-slate-900/80">
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
          Generate blog draft
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Create a new draft from campsite info and optional raw forecast input.
        </p>
      </div>

      {error ? (
        <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-300">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-300">
          {success}
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <FieldLabel>Type</FieldLabel>
          <Select
            value={form.type}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                type: e.target.value,
                compareCampsite:
                  e.target.value === "weather_comparison" ? prev.compareCampsite : "",
              }))
            }
          >
            <option value="weather_comparison">weather_comparison</option>
            <option value="campsite_weather">campsite_weather</option>
          </Select>
        </div>

        <div>
          <FieldLabel>Language</FieldLabel>
          <Select
            value={form.lang}
            onChange={(e) => setForm((prev) => ({ ...prev, lang: e.target.value }))}
          >
            <option value="en">English</option>
            <option value="is">Icelandic</option>
          </Select>
        </div>

        <div>
          <FieldLabel>Base campsite</FieldLabel>
          <TextInput
            value={form.baseCampsite}
            onChange={(e) => setForm((prev) => ({ ...prev, baseCampsite: e.target.value }))}
            placeholder="e.g. Vík Campsite"
          />
        </div>

        <div>
          <FieldLabel>Compare campsite</FieldLabel>
          <TextInput
            value={form.compareCampsite}
            onChange={(e) => setForm((prev) => ({ ...prev, compareCampsite: e.target.value }))}
            placeholder={
              form.type === "weather_comparison"
                ? "e.g. Skógar Campsite"
                : "Not used for campsite_weather"
            }
            disabled={form.type !== "weather_comparison"}
          />
        </div>

        <div className="md:col-span-2">
          <FieldLabel>Region</FieldLabel>
          <TextInput
            value={form.region}
            onChange={(e) => setForm((prev) => ({ ...prev, region: e.target.value }))}
            placeholder="e.g. South Iceland"
          />
        </div>

        <div className="md:col-span-2">
          <FieldLabel>Cover image URL</FieldLabel>
          <TextInput
            value={form.coverImage}
            onChange={(e) => setForm((prev) => ({ ...prev, coverImage: e.target.value }))}
            placeholder="https://..."
            type="url"
          />
        </div>

        <div className="md:col-span-2">
          <FieldLabel>Forecast raw input</FieldLabel>
          <TextArea
            value={form.forecastRawInput}
            onChange={(e) => setForm((prev) => ({ ...prev, forecastRawInput: e.target.value }))}
            rows={8}
            placeholder={`Paste raw forecast lines here, for example:

              mið. 08. apr. Él 2.4°C 5.3°C 9.2 m/s 2.5 mm
              fim. 09. apr. Él 1.4°C 5.6°C 10.9 m/s 2.1 mm gust 16.9 m/s
              fös. 10. apr. Smávægileg úrkoma -1.5°C 4.6°C 6.8 m/s 0.1 mm
              lau. 11. apr. Smávægileg úrkoma -4.2°C 0.8°C 7.7 m/s 0.2 mm
              sun. 12. apr. Smávægileg úrkoma -0.5°C 2.4°C 5.7 m/s 0.7 mm
              `}
          />
        </div>
      </div>

      <div className="mt-5">
        <button
          type="button"
          disabled={disabled}
          onClick={handleGenerate}
          className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-slate-950"
        >
          {loading ? "Generating..." : "Generate draft"}
        </button>
      </div>
    </section>
  );
}

function ManualCreateCard({ onCreated }) {
  const { lang } = useLanguage();
  const [form, setForm] = useState({
    language: "is",
    title: "",
    excerpt: "",
    coverImage: "",
    metaTitle: "",
    metaDescription: "",
    content: "",
  });
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Image upload — shared pipeline for both the file picker and clipboard
  // paste. textareaRef gives cursor-position access for Markdown insertion;
  // a plain <textarea> is used for the content field instead of the shared
  // TextArea wrapper specifically so this ref reaches the real DOM node.
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploadStatus, setUploadStatus] = useState(null); // { type: "uploading" | "error" } | null

  const disabled = saving || !form.title.trim() || !form.content.trim();

  function insertAtCursor(markdown) {
    const el = textareaRef.current;
    if (!el) {
      setForm((prev) => ({ ...prev, content: prev.content + markdown }));
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    setForm((prev) => ({
      ...prev,
      content: prev.content.slice(0, start) + markdown + prev.content.slice(end),
    }));
    requestAnimationFrame(() => {
      const node = textareaRef.current;
      if (!node) return;
      const pos = start + markdown.length;
      node.focus();
      node.setSelectionRange(pos, pos);
    });
  }

  async function uploadImage(file) {
    if (uploadStatus?.type === "uploading") return; // prevent duplicate concurrent uploads

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setUploadStatus({
        type: "error",
        message:
          lang === "is"
            ? "Óstudd skráargerð — aðeins JPEG, PNG eða WebP."
            : "Unsupported file type — JPEG, PNG or WebP only.",
      });
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setUploadStatus({
        type: "error",
        message: lang === "is" ? "Skráin er of stór (hámark 10 MB)." : "File is too large (10 MB max).",
      });
      return;
    }

    setUploadStatus({ type: "uploading" });

    const rawName = file.name || `pasted-image${extensionForMime(file.type)}`;
    const pathname = `blog-media/${sanitizeFilenameForPathname(rawName)}`;

    try {
      const blob = await upload(pathname, file, {
        access: "public",
        handleUploadUrl: "/api/blog-upload",
        contentType: file.type,
      });

      const metaRes = await fetch("/api/admin", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "createBlogMedia",
          url: blob.url,
          originalFilename: file.name || null,
        }),
      });
      const metaJson = await metaRes.json().catch(() => ({}));

      if (!metaRes.ok || !metaJson?.ok) {
        throw new Error(
          metaJson?.error || (lang === "is" ? "Tókst ekki að vista mynd-metadata" : "Failed to save image metadata")
        );
      }

      // Only insert Markdown once BOTH the Blob upload and the blog_media
      // persistence have succeeded — never point Markdown at an upload
      // whose metadata failed to save.
      insertAtCursor(`![](${blob.url})\n`);
      setUploadStatus(null);
    } catch (err) {
      setUploadStatus({
        type: "error",
        message: err?.message || (lang === "is" ? "Tókst ekki að hlaða upp mynd" : "Failed to upload image"),
      });
    }
  }

  function handleFileInputChange(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file afterward
    if (file) uploadImage(file);
  }

  function handlePaste(e) {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageItem = Array.from(items).find((item) => item.type?.startsWith("image/"));
    if (!imageItem) return; // no image in clipboard — let normal text paste proceed untouched
    e.preventDefault();
    const file = imageItem.getAsFile();
    if (file) uploadImage(file);
  }

  async function handleCreate() {
    setSaving(true);
    setError("");
    setSuccess("");

    const result = await onCreated({
      language: form.language,
      title: form.title.trim(),
      excerpt: form.excerpt.trim(),
      coverImage: form.coverImage.trim(),
      metaTitle: form.metaTitle.trim(),
      metaDescription: form.metaDescription.trim(),
      content: form.content,
    });

    if (result?.ok) {
      setSuccess(lang === "is" ? `Drög búin til: "${result.post.title}"` : `Draft created: "${result.post.title}"`);
      setForm({
        language: form.language,
        title: "",
        excerpt: "",
        coverImage: "",
        metaTitle: "",
        metaDescription: "",
        content: "",
      });
      setShowPreview(false);
    } else {
      setError(result?.error || (lang === "is" ? "Tókst ekki að búa til færslu" : "Failed to create post"));
    }

    setSaving(false);
  }

  return (
    <section className="rounded-3xl border border-slate-200/70 bg-white/80 p-5 shadow-sm dark:border-slate-800/80 dark:bg-slate-900/80">
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
          {lang === "is" ? "Skrifa bloggfærslu handvirkt" : "Write blog post manually"}
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {lang === "is"
            ? "Stofnar drög — birt með sömu Publish-aðgerð og AI-generated færslur."
            : "Creates a draft — published with the same Publish action as AI-generated posts."}
        </p>
      </div>

      {error ? (
        <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-300">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-300">
          {success}
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <FieldLabel>{lang === "is" ? "Tungumál" : "Language"}</FieldLabel>
          <Select
            value={form.language}
            onChange={(e) => setForm((prev) => ({ ...prev, language: e.target.value }))}
          >
            <option value="is">Icelandic</option>
            <option value="en">English</option>
          </Select>
        </div>

        <div>
          <FieldLabel>Title</FieldLabel>
          <TextInput
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
          />
        </div>

        <div className="md:col-span-2">
          <FieldLabel>Excerpt</FieldLabel>
          <TextArea
            value={form.excerpt}
            onChange={(e) => setForm((prev) => ({ ...prev, excerpt: e.target.value }))}
            rows={2}
          />
        </div>

        <div>
          <FieldLabel>{lang === "is" ? "Forsíðumynd (URL)" : "Cover image URL"}</FieldLabel>
          <TextInput
            value={form.coverImage}
            onChange={(e) => setForm((prev) => ({ ...prev, coverImage: e.target.value }))}
            placeholder="https://..."
            type="url"
          />
        </div>

        <div>
          <FieldLabel>Meta title</FieldLabel>
          <TextInput
            value={form.metaTitle}
            onChange={(e) => setForm((prev) => ({ ...prev, metaTitle: e.target.value }))}
            placeholder={lang === "is" ? "SEO titill (sjálfgefið = titill)" : "SEO title (defaults to post title if blank)"}
          />
        </div>

        <div className="md:col-span-2">
          <FieldLabel>Meta description</FieldLabel>
          <TextArea
            value={form.metaDescription}
            onChange={(e) => setForm((prev) => ({ ...prev, metaDescription: e.target.value }))}
            rows={2}
          />
        </div>

        <div className="md:col-span-2">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <FieldLabel>{lang === "is" ? "Efni (Markdown)" : "Content (Markdown)"}</FieldLabel>
            <div className="mb-2 flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleFileInputChange}
              />
              <button
                type="button"
                disabled={uploadStatus?.type === "uploading"}
                onClick={() => fileInputRef.current?.click()}
                className="text-xs font-semibold text-sky-600 underline hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50 dark:text-sky-400"
              >
                {uploadStatus?.type === "uploading"
                  ? (lang === "is" ? "Hleð upp..." : "Uploading...")
                  : (lang === "is" ? "Hlaða upp mynd" : "Upload image")}
              </button>
              <button
                type="button"
                onClick={() => setShowPreview((v) => !v)}
                className="text-xs font-semibold text-sky-600 underline hover:text-sky-700 dark:text-sky-400"
              >
                {showPreview ? (lang === "is" ? "Fela forskoðun" : "Hide preview") : (lang === "is" ? "Forskoða" : "Preview")}
              </button>
            </div>
          </div>
          {uploadStatus?.type === "error" && (
            <div className="mb-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-300">
              {uploadStatus.message}
            </div>
          )}
          <textarea
            ref={textareaRef}
            value={form.content}
            onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
            onPaste={handlePaste}
            rows={14}
            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none ring-0 focus:border-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
          />
          <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
            {lang === "is"
              ? "Þú getur líka límt mynd beint úr klippiborði inn í reitinn."
              : "You can also paste an image directly from your clipboard into this field."}
          </p>
          {showPreview && (
            <div className="mt-3 rounded-2xl border border-slate-200/70 bg-slate-50/60 px-4 dark:border-slate-700/70 dark:bg-slate-800/40">
              <BlogContent content={form.content} isLight={true} />
            </div>
          )}
        </div>
      </div>

      <div className="mt-5">
        <button
          type="button"
          disabled={disabled}
          onClick={handleCreate}
          className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-slate-950"
        >
          {saving ? (lang === "is" ? "Vista..." : "Saving...") : (lang === "is" ? "Búa til drög" : "Create draft")}
        </button>
      </div>
    </section>
  );
}

function BlogEditorCard({ post, pairedPost, onSave, onPublish, onDelete, onCancel, saving, publishing }) {
  const { lang } = useLanguage();
  const t = useT(lang);
  const [draft, setDraft] = useState({
    title: post.title || "",
    excerpt: post.excerpt || "",
    coverImage: post.coverImage || "",
    metaTitle: post.metaTitle || "",
    metaDescription: post.metaDescription || "",
    ctaHint: post.ctaHint || "",
    sourceType: post.sourceType || "manual",
    topic: post.topic || "",
    ctaTitle: post.ctaTitle || "",
    ctaText: post.ctaText || "",
    ctaButton: post.ctaButton || "",
    ctaTarget: post.ctaTarget || "",
    nearbyAttractions: post.nearbyAttractions || "",
    content: post.content || "",
  });

  useEffect(() => {
    setDraft({
      title: post.title || "",
      excerpt: post.excerpt || "",
      coverImage: post.coverImage || "",
      metaTitle: post.metaTitle || "",
      metaDescription: post.metaDescription || "",
      ctaHint: post.ctaHint || "",
      sourceType: post.sourceType || "manual",
      topic: post.topic || "",
      ctaTitle: post.ctaTitle || "",
      ctaText: post.ctaText || "",
      ctaButton: post.ctaButton || "",
      ctaTarget: post.ctaTarget || "",
      nearbyAttractions: post.nearbyAttractions || "",
      content: post.content || "",
    });
  }, [post.id, post.title, post.excerpt, post.coverImage, post.metaTitle, post.metaDescription, post.ctaHint, post.sourceType, post.topic, post.ctaTitle, post.ctaText, post.ctaButton, post.ctaTarget, post.nearbyAttractions, post.content]);

  const dirty = useMemo(() => {
    return (
      draft.title !== (post.title || "") ||
      draft.excerpt !== (post.excerpt || "") ||
      draft.coverImage !== (post.coverImage || "") ||
      draft.metaTitle !== (post.metaTitle || "") ||
      draft.metaDescription !== (post.metaDescription || "") ||
      draft.ctaHint !== (post.ctaHint || "") ||
      draft.sourceType !== (post.sourceType || "manual") ||
      draft.topic !== (post.topic || "") ||
      draft.ctaTitle !== (post.ctaTitle || "") ||
      draft.ctaText !== (post.ctaText || "") ||
      draft.ctaButton !== (post.ctaButton || "") ||
      draft.ctaTarget !== (post.ctaTarget || "") ||
      draft.nearbyAttractions !== (post.nearbyAttractions || "") ||
      draft.content !== (post.content || "")
    );
  }, [draft, post]);

  return (
    <section className="rounded-3xl border border-slate-200/70 bg-white/80 p-5 shadow-sm dark:border-slate-800/80 dark:bg-slate-900/80">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {post.title || "Untitled draft"}
            </span>

            <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
              {post.status || "draft"}
            </span>
            <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold uppercase text-sky-700 dark:bg-sky-950/50 dark:text-sky-300">
              {post.language || "is"}
            </span>
          </div>

          <div className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            slug: <span className="font-mono">{post.slug}</span>
          </div>

          <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            publishedAt: {formatDateTime(post.publishedAt)}
          </div>

          <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            updatedAt: {formatDateTime(post.updatedAt)}
          </div>

          {post.translationGroupId && (
            <div className="mt-1 text-xs text-slate-400 dark:text-slate-500 font-mono">
              group: {post.translationGroupId.slice(0, 8)}…
            </div>
          )}

          <div className="mt-1 text-sm">
            {pairedPost ? (
              <span className="text-emerald-600 dark:text-emerald-400">
                ✓ Þýtt — {post.language === "en" ? "IS" : "EN"}: {pairedPost.title || pairedPost.slug}
              </span>
            ) : (
              <span className="text-slate-400 dark:text-slate-500">— Vantar þýðingu</span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {post.status === "draft" && (
            <a
              href={`/blog/${post.slug}?preview=draft`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              {t("blogPreviewButton")}
            </a>
          )}
          {post.status === "published" && (
            <a
              href={`/blog/${post.slug}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Open post
            </a>
          )}
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(post.id)}
              className="rounded-xl border border-rose-300 bg-rose-50 px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-300 dark:hover:bg-rose-950/40"
            >
              Delete
            </button>
          )}
          <button
            type="button"
            disabled={!dirty || saving}
            onClick={() => onSave(post.id, { ...draft })}
            className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-slate-950"
          >
            {saving ? "Saving..." : "Save changes"}
          </button>
          {onPublish && (
            <button
              type="button"
              disabled={publishing}
              onClick={() => onPublish(post.id)}
              className="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-800 disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300"
            >
              {publishing ? "Publishing..." : "Publish"}
            </button>
          )}
        </div>
      </div>

      <div className="mt-5 grid gap-4">
        <div className="grid gap-3 rounded-2xl border border-slate-200/70 bg-slate-50/60 p-3 dark:border-slate-700/70 dark:bg-slate-800/40 md:grid-cols-2">
          <div>
            <FieldLabel>Language (read-only)</FieldLabel>
            <TextInput value={post.language || "is"} disabled />
          </div>
          <div>
            <FieldLabel>Translation group (read-only)</FieldLabel>
            <TextInput
              value={post.translationGroupId ? `${post.translationGroupId.slice(0, 8)}…` : "—"}
              disabled
            />
          </div>
          {pairedPost && (
            <div className="md:col-span-2">
              <FieldLabel>Paired {post.language === "en" ? "IS" : "EN"} post</FieldLabel>
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
                  {pairedPost.title || pairedPost.slug}
                </span>
                <a
                  href={`/blog/${pairedPost.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 text-xs text-sky-600 underline hover:text-sky-700 dark:text-sky-400"
                >
                  Open ↗
                </a>
              </div>
            </div>
          )}
        </div>

        <div>
          <FieldLabel>Title</FieldLabel>
          <TextInput
            value={draft.title}
            onChange={(e) => setDraft((prev) => ({ ...prev, title: e.target.value }))}
          />
        </div>

        <div>
          <FieldLabel>Excerpt</FieldLabel>
          <TextArea
            value={draft.excerpt}
            onChange={(e) => setDraft((prev) => ({ ...prev, excerpt: e.target.value }))}
            rows={3}
          />
        </div>

        <div>
          <FieldLabel>Cover image URL</FieldLabel>
          <TextInput
            value={draft.coverImage}
            onChange={(e) => setDraft((prev) => ({ ...prev, coverImage: e.target.value }))}
            placeholder="https://..."
            type="url"
          />
        </div>

        <div>
          <FieldLabel>Meta title</FieldLabel>
          <TextInput
            value={draft.metaTitle}
            onChange={(e) => setDraft((prev) => ({ ...prev, metaTitle: e.target.value }))}
            placeholder="SEO title (defaults to post title if blank)"
          />
        </div>

        <div>
          <FieldLabel>Meta description</FieldLabel>
          <TextArea
            value={draft.metaDescription}
            onChange={(e) => setDraft((prev) => ({ ...prev, metaDescription: e.target.value }))}
            rows={2}
            placeholder="SEO description"
          />
        </div>

        <div>
          <FieldLabel>CTA hint</FieldLabel>
          <TextInput
            value={draft.ctaHint}
            onChange={(e) => setDraft((prev) => ({ ...prev, ctaHint: e.target.value }))}
            placeholder="e.g. Check the forecast"
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <FieldLabel>{lang === "is" ? "Tegund" : "Source type"}</FieldLabel>
            <Select
              value={draft.sourceType}
              onChange={(e) => setDraft((prev) => ({ ...prev, sourceType: e.target.value }))}
            >
              <option value="manual">manual</option>
              <option value="automated">automated</option>
            </Select>
          </div>

          <div>
            <FieldLabel>{lang === "is" ? "Efni" : "Topic"}</FieldLabel>
            <TextInput
              value={draft.topic}
              onChange={(e) => setDraft((prev) => ({ ...prev, topic: e.target.value }))}
              placeholder={lang === "is" ? "t.d. veður, ferðalög" : "e.g. weather, travel"}
            />
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200/70 bg-slate-50/60 p-4 dark:border-slate-700/70 dark:bg-slate-800/40">
          <div className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-300">
            {lang === "is" ? "Sérsniðið CTA" : "Custom CTA"}
          </div>
          <div className="grid gap-3">
            <div>
              <FieldLabel>{lang === "is" ? "CTA titill" : "CTA title"}</FieldLabel>
              <TextInput
                value={draft.ctaTitle}
                onChange={(e) => setDraft((prev) => ({ ...prev, ctaTitle: e.target.value }))}
                placeholder={lang === "is" ? "Hnekkir sjálfgefnum titli ef fyllt út" : "Overrides global default if filled in"}
              />
            </div>
            <div>
              <FieldLabel>{lang === "is" ? "CTA texti" : "CTA text"}</FieldLabel>
              <TextArea
                value={draft.ctaText}
                onChange={(e) => setDraft((prev) => ({ ...prev, ctaText: e.target.value }))}
                rows={2}
                placeholder={lang === "is" ? "Hnekkir sjálfgefnum texta ef fyllt út" : "Overrides global default if filled in"}
              />
            </div>
            <div>
              <FieldLabel>{lang === "is" ? "CTA takki" : "CTA button"}</FieldLabel>
              <TextInput
                value={draft.ctaButton}
                onChange={(e) => setDraft((prev) => ({ ...prev, ctaButton: e.target.value }))}
                placeholder={lang === "is" ? "Hnekkir sjálfgefnum takka ef fyllt út" : "Overrides global default if filled in"}
              />
            </div>
            <div>
              <FieldLabel>{lang === "is" ? "CTA slóð" : "CTA target URL"}</FieldLabel>
              <TextInput
                value={draft.ctaTarget}
                onChange={(e) => setDraft((prev) => ({ ...prev, ctaTarget: e.target.value }))}
                placeholder="e.g. /pricing"
              />
            </div>
          </div>
        </div>

        <div>
          <FieldLabel>{lang === "is" ? "Nálægir staðir" : "Nearby places"}</FieldLabel>
          <TextArea
            value={draft.nearbyAttractions}
            onChange={(e) => setDraft((prev) => ({ ...prev, nearbyAttractions: e.target.value }))}
            rows={4}
            placeholder={lang === "is" ? "Ein lína á hverja staðsetningu / One item per line" : "One item per line"}
          />
        </div>

        <div>
          <FieldLabel>Content</FieldLabel>
          <TextArea
            value={draft.content}
            onChange={(e) => setDraft((prev) => ({ ...prev, content: e.target.value }))}
            rows={14}
          />
        </div>
      </div>
    </section>
  );
}

function PublishedPostRow({ post, pairedPost, onEdit }) {
  return (
    <div className="rounded-2xl border border-slate-200/70 bg-white/80 p-4 shadow-sm dark:border-slate-800/80 dark:bg-slate-900/80">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-base font-semibold text-slate-900 dark:text-slate-100">
              {post.title || "Untitled"}
            </h3>
            <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300">
              published
            </span>
            <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold uppercase text-sky-700 dark:bg-sky-950/50 dark:text-sky-300">
              {post.language || "is"}
            </span>
          </div>

          <div className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            slug: <span className="font-mono">{post.slug}</span>
          </div>

          <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            publishedAt: {formatDateTime(post.publishedAt)}
          </div>

          <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            updatedAt: {formatDateTime(post.updatedAt)}
          </div>

          <div className="mt-1 text-sm">
            {pairedPost ? (
              <span className="text-emerald-600 dark:text-emerald-400">
                ✓ Þýtt — {(post.language === "en" ? "IS" : "EN")}: {pairedPost.title || pairedPost.slug}
              </span>
            ) : (
              <span className="text-slate-400 dark:text-slate-500">— Vantar þýðingu</span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => onEdit(post.id)}
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Edit
          </button>
          <Link
            to={`/blog/${post.slug}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
          >
            Open post
          </Link>
        </div>
      </div>

      {post.excerpt ? (
        <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">{post.excerpt}</p>
      ) : null}
    </div>
  );
}

export default function AdminDashboard() {
  const [state, setState] = useState({
    loading: true,
    error: "",
    data: null,
  });

  const {
    posts,
    loading: loadingPosts,
    savingId,
    publishingId,
    error: blogError,
    reloadPosts,
    createPost,
    updatePost,
    publishPost,
    deletePost,
  } = useAdminBlogPosts();

  const { lang } = useLanguage();
  const [createMode, setCreateMode] = useState("generate");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await fetch("/api/admin", {
          credentials: "include",
          cache: "no-store",
        });

        const json = await res.json().catch(() => ({}));

        if (!res.ok || !json?.ok) {
          throw new Error(json?.error || "Failed to load admin summary");
        }

        if (!cancelled) {
          setState({
            loading: false,
            error: "",
            data: json,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            loading: false,
            error: err?.message || "Failed to load admin summary",
            data: null,
          });
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  async function handleGenerated() {
    await reloadPosts();
  }

  const { users, pro, revenue } = state.data || {
    users: { total: 0, new7d: 0, new30d: 0 },
    pro: { active: 0, expired: 0, conversionRate: 0 },
    revenue: { month: 0, last30d: 0, lifetime: 0 },
  };

  const [editingPublishedId, setEditingPublishedId] = useState(null);

  const draftPosts = posts.filter((post) => post.status !== "published");
  const publishedPosts = posts.filter((post) => post.status === "published");

  const translationPairMap = useMemo(() => {
    const map = {};
    for (const p of posts) {
      if (!p.translationGroupId) continue;
      if (!map[p.translationGroupId]) map[p.translationGroupId] = [];
      map[p.translationGroupId].push(p);
    }
    return map;
  }, [posts]);

  function getPairedPost(post) {
    if (!post.translationGroupId) return null;
    const group = translationPairMap[post.translationGroupId] || [];
    return group.find((p) => p.id !== post.id) || null;
  }

  if (state.loading) {
    return (
      <main className="min-h-screen bg-soft-grid text-slate-900 dark:bg-slate-950 dark:text-slate-100">
        <div className="mx-auto max-w-6xl px-4 py-10">
          <div className="rounded-3xl border border-slate-200/70 bg-white/75 p-6 shadow-sm backdrop-blur dark:border-slate-800/80 dark:bg-slate-900/75">
            <h1 className="text-2xl font-semibold">CampCast Admin</h1>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Hleð admin yfirliti...
            </p>
          </div>
        </div>
      </main>
    );
  }

  if (state.error) {
    return (
      <main className="min-h-screen bg-soft-grid text-slate-900 dark:bg-slate-950 dark:text-slate-100">
        <div className="mx-auto max-w-6xl px-4 py-10">
          <div className="rounded-3xl border border-rose-200/80 bg-white/80 p-6 shadow-sm dark:border-rose-900/70 dark:bg-slate-900/80">
            <h1 className="text-2xl font-semibold">CampCast Admin</h1>
            <p className="mt-3 text-sm text-rose-600 dark:text-rose-400">
              Gat ekki hlaðið admin gögnum: {state.error}
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-soft-grid text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <div className="mx-auto max-w-6xl px-4 py-8 md:py-10">
        <header className="mb-6 rounded-3xl border border-slate-200/70 bg-white/75 p-6 shadow-sm backdrop-blur dark:border-slate-800/80 dark:bg-slate-900/75">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="inline-flex items-center rounded-full border border-emerald-200/70 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/50 dark:text-emerald-300">
                Internal admin
              </div>
              <h1 className="mt-3 text-2xl font-semibold md:text-3xl">CampCast Admin</h1>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                Admin yfirlit yfir notendur, áskriftir og blog workflow.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 md:w-[340px]">
              <SummaryPill label="Users" value={users.total} />
              <SummaryPill label="Active Pro" value={pro.active} />
            </div>
          </div>
        </header>

        <div className="grid gap-4 md:grid-cols-3">
          <Card title="Users" subtitle="Notendavöxtur og nýskráningar">
            <StatRow label="Total users" value={users.total} />
            <StatRow label="New last 7 days" value={`+${users.new7d}`} />
            <StatRow label="New last 30 days" value={`+${users.new30d}`} />
          </Card>

          <Card title="Pro" subtitle="Yfirlit yfir áskriftastöðu">
            <StatRow label="Active subscriptions" value={pro.active} />
            <StatRow label="Expired subscriptions" value={pro.expired} />
            <StatRow label="Conversion rate" value={formatPercent(pro.conversionRate)} />
          </Card>

          <Card title="Revenue" subtitle="Kemur þegar revenue persistence er tilbúin">
            <StatRow label="This month" value={formatMoney(revenue.month)} muted />
            <StatRow label="Last 30 days" value={formatMoney(revenue.last30d)} muted />
            <StatRow label="Lifetime" value={formatMoney(revenue.lifetime)} muted />
          </Card>
        </div>

        <section className="mt-8">
          <div className="mb-4 flex gap-2">
            <button
              type="button"
              onClick={() => setCreateMode("generate")}
              className={`rounded-xl px-4 py-2 text-sm font-medium ${
                createMode === "generate"
                  ? "bg-slate-900 text-white dark:bg-white dark:text-slate-950"
                  : "border border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              }`}
            >
              {lang === "is" ? "Generate-a með AI" : "Generate with AI"}
            </button>
            <button
              type="button"
              onClick={() => setCreateMode("manual")}
              className={`rounded-xl px-4 py-2 text-sm font-medium ${
                createMode === "manual"
                  ? "bg-slate-900 text-white dark:bg-white dark:text-slate-950"
                  : "border border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              }`}
            >
              {lang === "is" ? "Skrifa handvirkt" : "Write manually"}
            </button>
          </div>

          {createMode === "generate" ? (
            <GenerateDraftCard onGenerated={handleGenerated} />
          ) : (
            <ManualCreateCard onCreated={createPost} />
          )}
        </section>

        <section className="mt-8">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Drafts</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Edit title, excerpt and content. Publish when ready.
            </p>
          </div>

          {blogError ? (
            <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-300">
              {blogError}
            </div>
          ) : null}

          {loadingPosts ? (
            <div className="rounded-3xl border border-slate-200/70 bg-white/75 p-6 text-sm text-slate-500 shadow-sm dark:border-slate-800/80 dark:bg-slate-900/75 dark:text-slate-400">
              Hleð blog póstum...
            </div>
          ) : draftPosts.length === 0 ? (
            <div className="rounded-3xl border border-slate-200/70 bg-white/75 p-6 text-sm text-slate-500 shadow-sm dark:border-slate-800/80 dark:bg-slate-900/75 dark:text-slate-400">
              Engin draft í vinnslu.
            </div>
          ) : (
            <div className="grid gap-5">
              {draftPosts.map((post) => (
                <BlogEditorCard
                  key={post.id}
                  post={post}
                  pairedPost={getPairedPost(post)}
                  saving={savingId === post.id}
                  publishing={publishingId === post.id}
                  onSave={updatePost}
                  onPublish={publishPost}
                  onDelete={deletePost}
                />
              ))}
            </div>
          )}
        </section>

        <section className="mt-8">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              Published posts
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Live blog posts. Open any post to review it on the site.
            </p>
          </div>

          {loadingPosts ? (
            <div className="rounded-3xl border border-slate-200/70 bg-white/75 p-6 text-sm text-slate-500 shadow-sm dark:border-slate-800/80 dark:bg-slate-900/75 dark:text-slate-400">
              Hleð published póstum...
            </div>
          ) : publishedPosts.length === 0 ? (
            <div className="rounded-3xl border border-slate-200/70 bg-white/75 p-6 text-sm text-slate-500 shadow-sm dark:border-slate-800/80 dark:bg-slate-900/75 dark:text-slate-400">
              Engir published póstar ennþá.
            </div>
          ) : (
            <div className="grid gap-4">
              {publishedPosts.map((post) =>
                editingPublishedId === post.id ? (
                  <BlogEditorCard
                    key={post.id}
                    post={post}
                    pairedPost={getPairedPost(post)}
                    saving={savingId === post.id}
                    onSave={updatePost}
                    onCancel={() => setEditingPublishedId(null)}
                  />
                ) : (
                  <PublishedPostRow
                    key={post.id}
                    post={post}
                    pairedPost={getPairedPost(post)}
                    onEdit={(id) => setEditingPublishedId(id)}
                  />
                )
              )}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
